import { createSlice } from '@reduxjs/toolkit'
import { StoreStatus } from 'common/utils/types'
import { RequestType, type StakeholderLimit, type StakeholderRequest } from 'common/requests/request.types'
import {
  getRequests,
  getLimits,
  rejectRequest,
  resolveRequest,
  resolveLimitRequest,
  editPlan,
  requestPlanChange,
} from 'common/requests/requests.service'

export interface RequestsState {
  getRequestsStatus: StoreStatus
  resolveRequestStatus: StoreStatus
  rejectRequestStatus: StoreStatus
  activeReportRequests: StakeholderRequest[] | null
  resolvedReportRequests: StakeholderRequest[] | null
  resolvedRequestsCount: number
  activeLimits: StakeholderLimit[] | null
  activeLimitsCount: number
  resolvedLimits: StakeholderLimit[] | null
  resolvedLimitsCount: number
  getLimitsStatus: StoreStatus
  resolveLimitRequestStatus: StoreStatus
  editPlanStatus: StoreStatus
  requestPlanChangeStatus: StoreStatus
}

const initialState: RequestsState = {
  getRequestsStatus: StoreStatus.Idle,
  resolveRequestStatus: StoreStatus.Idle,
  activeReportRequests: null,
  resolvedReportRequests: null,
  resolvedRequestsCount: 0,
  activeLimits: null,
  activeLimitsCount: 0,
  resolvedLimits: null,
  resolvedLimitsCount: 0,
  getLimitsStatus: StoreStatus.Idle,
  rejectRequestStatus: StoreStatus.Idle,
  resolveLimitRequestStatus: StoreStatus.Idle,
  editPlanStatus: StoreStatus.Idle,
  requestPlanChangeStatus: StoreStatus.Idle,
}

export const RequestsSlice = createSlice({
  name: 'requests',
  initialState,
  reducers: {
    resetState: (state) => ({
      ...initialState,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(getRequests.pending, (state, { payload }) => ({
      ...state,
      getRequestsStatus: StoreStatus.Loading,
    }))
    builder.addCase(getRequests.fulfilled, (state, { payload, meta }) => {
      if( meta.arg.status === 'active') {
        return {
          ...state,
          activeReportRequests: payload.data,
          getRequestsStatus: StoreStatus.Succeeded,
        }
      } else if( meta.arg.status === 'resolved') {
        return {
          ...state,
          resolvedRequestsCount: payload.count,
          resolvedReportRequests: payload.data,
          getRequestsStatus: StoreStatus.Succeeded,
        }
      }
    })
    builder.addCase(getRequests.rejected, (state) => ({
      ...state,
      activeReportRequests: null,
      resolvedReportRequests: null,
      getRequestsStatus: StoreStatus.Failed,
    }))
    builder.addCase(resolveRequest.pending, (state, { payload }) => ({
      ...state,
      resolveRequestStatus: StoreStatus.Loading,
    }))
    builder.addCase(resolveRequest.fulfilled, (state, { payload }) => {
      const resolvedRequest = state.activeReportRequests?.find((r) => r.id === payload)
      const updatedResolvedRequests = resolvedRequest
      ? [resolvedRequest, ...state.resolvedReportRequests ?? []]
      : state.resolvedReportRequests
      return {
          ...state,
          activeReportRequests: state.activeReportRequests?.filter((r) => r.id !== payload) ?? [],
          resolvedReportRequests: updatedResolvedRequests,
          resolveLimitRequestStatus: StoreStatus.Succeeded,
        }
      })
    builder.addCase(resolveRequest.rejected, (state) => ({
      ...state,
      activeReportRequests: [],
      resolvedReportRequests: [],
      resolveRequestStatus: StoreStatus.Failed,
    }))
    builder.addCase(requestPlanChange.pending, (state, { payload }) => ({
      ...state,
      requestPlanChangeStatus: StoreStatus.Loading,
    }))
    builder.addCase(requestPlanChange.fulfilled, (state, { payload }) => ({
      ...state,
      requestPlanChangeStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(requestPlanChange.rejected, (state) => ({
      ...state,
      requestPlanChangeStatus: StoreStatus.Failed,
    }))
    builder.addCase(resolveLimitRequest.pending, (state, { payload }) => ({
      ...state,
      resolveLimitRequestStatus: StoreStatus.Loading,
    }))
    builder.addCase(resolveLimitRequest.fulfilled, (state, { payload }) => {
    const resolvedRequest = state.activeLimits?.find((r) => r.id === payload)
    const updatedResolvedLimits = resolvedRequest
    ? [resolvedRequest, ...state.resolvedLimits ?? []]
    : state.resolvedLimits
    return {
        ...state,
        activeLimits: state.activeLimits?.filter((r) => r.id !== payload) ?? [],
        resolvedLimits: updatedResolvedLimits,
        resolveLimitRequestStatus: StoreStatus.Succeeded,
      }
    })
    builder.addCase(resolveLimitRequest.rejected, (state) => ({
      ...state,
      activeLimits: [],
      resolvedLimits: [],
      resolveLimitRequestStatus: StoreStatus.Failed,
    }))

    builder.addCase(getLimits.pending, (state, { payload }) => ({
      ...state,
      getLimitsStatus: StoreStatus.Loading,
    }))
    builder.addCase(getLimits.fulfilled, (state, { payload, meta }) => {
      if( meta.arg.status === 'active') {
        return {
          ...state,
          activeLimits: payload.data,
          activeLimitsCount: payload.count,
          getLimitsStatus: StoreStatus.Succeeded,
        }
      } else if( meta.arg.status === 'resolved') {
        return {
          ...state,
          resolvedLimits: payload.data,
          resolvedLimitsCount: payload.count,
          getLimitsStatus: StoreStatus.Succeeded,
        }
      }
    })
    builder.addCase(getLimits.rejected, (state) => ({
      ...state,
      resolvedLimits: null,
      activeLimits: null,
      getLimitsStatus: StoreStatus.Failed,
    }))
    builder.addCase(editPlan.pending, (state, { payload }) => ({
      ...state,
      editPlanStatus: StoreStatus.Loading,
    }))
    builder.addCase(editPlan.fulfilled, (state, { payload }) => ({
      ...state,
      editPlanStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(editPlan.rejected, (state) => ({
      ...state,
      editPlanStatus: StoreStatus.Failed,
    }))

    builder.addCase(rejectRequest.pending, (state, { payload }) => ({
      ...state,
      rejectRequestStatus: StoreStatus.Loading,
    }))
    builder.addCase(rejectRequest.fulfilled, (state, { payload }) => {
      let requestsUpdate = state.activeReportRequests
      let limitsUpdate = state.activeLimits
      if (payload.data.type === RequestType.shareRequest) {
        requestsUpdate = state.activeReportRequests?.filter((r) => r.id !== payload.requestId) ?? []
      } else if (payload.data.type === RequestType.limitRequest) {
        limitsUpdate = state.activeLimits?.filter((r) => r.id !== payload.requestId) ?? []
      }
      return {
        ...state,
        activeReportRequests: requestsUpdate,
        activeLimits: limitsUpdate,
        rejectRequestStatus: StoreStatus.Succeeded,
      }
    })
    builder.addCase(rejectRequest.rejected, (state) => ({
      ...state,
      activeReportRequests: [],
      rejectRequestStatus: StoreStatus.Failed,
    }))
  },
})

export const { resetState } = RequestsSlice.actions

export default RequestsSlice.reducer
